import { ReactNode, useState } from 'react'

import { useAtomValue } from 'jotai'

import { useNotifications } from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

import {
  invoiceAccountingDateAtom,
  invoiceCustomerBillingAddressAtom,
  invoiceCustomerLegalCompanyNameAtom,
  invoiceCustomerTaxStatusAtom,
  invoiceHasNegativeTotalAtom,
  invoiceIdAtom,
  isSavePendingAtom,
  lineItemGroupsAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { InvoiceConfirmationModal } from 'Invoices/components/LegacyInvoiceModals'
import { useInvalidateInvoice } from 'Invoices/hooks/useInvalidateInvoice'
import { useTaxIntegrationProvider } from 'lib/hooks/useTaxIntegrationProvider.ts'
import { getApiErrorMessage } from 'Cube/utils/getApiErrorMessage'

type Props = {
  trigger: ({
    onClick,
    disabled
  }: {
    onClick: () => void
    disabled: boolean
  }) => ReactNode
}

export const FinalizeModal = ({ trigger }: Props) => {
  const { displayPromiseNotification } = useNotifications()
  const invalidateInvoice = useInvalidateInvoice()
  const { isAnrok } = useTaxIntegrationProvider()

  const [showModal, setShowModal] = useState<boolean>(false)

  const invoiceId = useAtomValue(invoiceIdAtom)
  const invoiceAccountingDate = useAtomValue(invoiceAccountingDateAtom)
  const customerTaxStatus = useAtomValue(invoiceCustomerTaxStatusAtom)
  const customerBillingAddress = useAtomValue(invoiceCustomerBillingAddressAtom)
  const customerLegalName = useAtomValue(invoiceCustomerLegalCompanyNameAtom)
  const lineItemGroups = useAtomValue(lineItemGroupsAtom)
  const hasNegativeTotal = useAtomValue(invoiceHasNegativeTotalAtom)
  const isSavePending = useAtomValue(isSavePendingAtom)

  const taxCategoriesPresentOnInvoice = (lineItemGroups
    ?.map(group => group.taxCategory)
    .filter(Boolean) ?? []) as Array<{ id: string; name: string }>

  const { mutateAsync: finalizeMutate, isPending: finalizeIsPending } =
    useMutation(dashboardv99990101Client.postFinalizeInvoice)

  const { mutateAsync: recalculateAsync } = useMutation(
    dashboard20240730Client.postRecalculateInvoice,
    {
      onSuccess: () => {
        invalidateInvoice()
      }
    }
  )

  const onConfirm = () => {
    if (!invoiceId) return

    displayPromiseNotification(finalizeMutate({ id: invoiceId }), {
      loading: 'Finalizing invoice...',
      success: 'Invoice finalized',
      error: (error: any) =>
        isAnrok
          ? getApiErrorMessage(error, 'Unable to finalize invoice')
          : 'Unable to finalize invoice'
    })
  }

  const recalculateInvoice = () => {
    if (!invoiceId) return

    // Return the promise so callers that await recalculation (the tax-rate
    // remediation step) don't advance before it resolves.
    return displayPromiseNotification(recalculateAsync({ id: invoiceId }), {
      loading: 'Recalculating invoice...',
      success: 'Invoice recalculated',
      error: 'Failed to recalculate invoice'
    })
  }

  return (
    <>
      {trigger({
        onClick: () => setShowModal(true),
        disabled: isSavePending || finalizeIsPending || hasNegativeTotal
      })}

      {showModal && (
        <InvoiceConfirmationModal
          onClose={() => setShowModal(false)}
          onConfirm={async () => {
            // Close immediately; progress and errors are tracked by the
            // finalize toast rather than the modal.
            setShowModal(false)
            void onConfirm()
          }}
          recalculateInvoice={recalculateInvoice}
          taxCategoriesOnInvoice={taxCategoriesPresentOnInvoice}
          customerTaxStatus={customerTaxStatus}
          customerCountry={customerBillingAddress?.country}
          customerState={customerBillingAddress?.state}
          invoiceAccountingDate={invoiceAccountingDate}
          blockedTitle="This invoice can't be finalized"
          validationSummaryText="There are a few things to sort out before you can finalize this invoice."
          confirmationSummaryText={`Lock this invoice and prevent editing. Once finalized, you can send this invoice to ${customerLegalName}.`}
          confirmTitleText="Finalize invoice"
          confirmActionText="Finalize invoice"
          submitting={finalizeIsPending}
        />
      )}
    </>
  )
}
